import React, { Suspense, lazy } from "react";
import { Redirect, Route } from "react-router";
import { useNetwork } from "./context/NetworkProvider";
import PageNotAccessibleComponent from "./components/Shared/PageNotAccessibleComponent";
import RightsGate from "./components/Shared/RightsGate";
import { ActivityTracker } from "./helpers/activityTracker";

import { Layout } from "./components/Layout";
import { AuthorisedLayout } from "./components/AuthorisedLayout";
import { ForgotPasswordComponent } from "./components/ForgotPasswordComponent";
import { RegisterComponent } from "./components/RegisterComponent";
import { LoginComponent } from "./components/LoginComponent";
import { ResetPasswordComponent } from "./components/ResetPasswordComponent";
import { DispatchComponent } from "./components/reservations/dispatch/DispatchComponent";
import { TrackingComponent } from "./components/tracking/TrackingComponent";
import { RatingComponent } from "./components/rating/RatingComponent";
import { FeedbackComponent } from "./components/feedback/FeedbackComponent";
import { HotelBookingPriority } from "./components/hotelbookingpriority/HotelBookingPriority";
import ActivityRawDataComponent from "./components/report/ActivityRawDataComponent";
import CrewActivityComponent from "./components/crewActivity/activity/CrewActivityComponent";
import { CrewActivityMobileViewContainer } from "./components/crewActivity/CrewActivityMobileViewContainer";
// Masters screens load on demand so they stay out of the main bundle.
const named = (loader, name) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));
const AirportComponent = lazy(() =>
  import("./components/airport/AirportComponent"),
);
const LocalVendorComponent = lazy(() =>
  import("./components/localVendor/LocalVendorComponent"),
);
const HotelsComponent = lazy(() =>
  import("./components/hotels/HotelsComponent"),
);
const StaffMembersComponent = lazy(() =>
  import("./components/staffMembers/StaffMembersComponent"),
);
const CrewRegistrationReport = lazy(() =>
  import("./components/crewRegistrationReport/CrewRegistrationReport"),
);
const CrewNotificationComponent = lazy(() =>
  import("./components/crewNotification/CrewNotificationComponent"),
);
const DashboardOverviewComponent = lazy(() =>
  import("./components/dashboardOverview/DashboardOverviewComponent"),
);
const CancelHotelReportComponent = lazy(() =>
  import("./components/cancelHotelReport/CancelHotelReportComponent"),
);
const StayHotelReportComponent = lazy(() =>
  import("./components/stayHotelReport/StayHotelReportComponent"),
);
const CrewAddressComponent = named(
  () => import("./components/staffAddress/CrewAddressComponent"),
  "CrewAddressComponent",
);
const UserComponent = named(
  () => import("./components/user/UserComponent"),
  "UserComponent",
);
const CrewActivityCodeComponent = lazy(() =>
  import("./components/crewActivity/CrewActivityCodeComponent"),
);
const NonFlyActivityLocationComponent = lazy(() =>
  import("./components/nonflyActivityLocation/NonFlyActivityLocationComponent"),
);
const TransportOverviewComponent = lazy(() =>
  import("./components/transportOverview/TransportOverviewComponent"),
);
const EmailSettingComponent = lazy(() =>
  import("./components/emailSetting/EmailSettingComponent"),
);
const TransportMatrixComponent = lazy(() =>
  import("./components/transportMatrix/TransportMatrixComponent"),
);
const AirlineSettingComponent = lazy(() =>
  import("./components/airlinesetting/AirlineSettingComponent"),
);
const AppMenuComponent = lazy(() => import("./components/appMenu/AppMenuComponent"));
const NonFlyTransportMatrixComponent = lazy(() =>
  import("./components/crewActivity/NonFlyTransportMatrixComponent"),
);

const PageLoading = () => (
  <div className="text-center p-4">
    <i className="fas fa-spinner fa-spin" /> Loading...
  </div>
);

function App() {
  const { isNetworkError, setIsNetworkError } = useNetwork();
  if (isNetworkError) {
    return (
      <PageNotAccessibleComponent onRetry={() => setIsNetworkError(false)} />
    );
  }
  const isAuthorized =
    sessionStorage.getItem("session_userId") === "" ||
    sessionStorage.getItem("session_userId") === null
      ? false
      : true;

  return (
    <>
      {isAuthorized === true ? (
        <AuthorisedLayout>
          <Suspense fallback={<PageLoading />}>
          <ActivityTracker />
          <RightsGate>
          <Route path="/dashboard-overview" component={DashboardOverviewComponent} />
          <Route path="/dashboard" render={() => <Redirect to="/dashboard-overview" />} />
          <Route path="/airport" component={AirportComponent} />
          <Route path="/tracking" component={TrackingComponent} />
          <Route path="/hotels" component={HotelsComponent} />
          <Route path="/local-vendor" component={LocalVendorComponent} />
          {/* Old URL kept so existing bookmarks still land on the page. */}
          <Route
            path="/service-providers"
            render={() => <Redirect to="/local-vendor" />}
          />
          <Route path="/dispatch" component={DispatchComponent} />
          <Route path="/crew-member" component={StaffMembersComponent} />
          <Route path="/user" component={UserComponent} />
          <Route path="/rating" component={RatingComponent} />
          <Route exact path="/feedback" component={FeedbackComponent} />
          <Route
            exact
            path="/feedback/comment"
            render={() => <Redirect to="/feedback" />}
          />
          <Route path="/notification" component={CrewNotificationComponent} />
          {/* Schedule Dashboard was removed (2026-10-31): old links open the Dashboard. */}
          <Route path="/scheduleDashboard" render={() => <Redirect to="/dashboard-overview" />} />
          <Route path="/config/transport-overview" component={TransportOverviewComponent} />
          <Route
            path="/config/hotel-transfer-contract"
            render={() => <Redirect to="/config/transport-overview" />}
          />
          <Route path="/config/email" component={EmailSettingComponent} />
          <Route path="/config/airline-setting" component={AirlineSettingComponent} />
          <Route path="/app-menu" component={AppMenuComponent} />
          <Route path="/report/cancel" component={CancelHotelReportComponent} />
          <Route path="/report/stay" component={StayHotelReportComponent} />
          <Route path="/report/crew-registration" component={CrewRegistrationReport} />
          <Route
            path="/config/booking/hotelpriority"
            component={HotelBookingPriority}
          />
          {/* Airport Geofence Report was removed (2026-10-31): old links open the Dashboard. */}
          <Route path="/report/geofence" render={() => <Redirect to="/dashboard-overview" />} />
          <Route
            path="/report/activity-rawdata"
            component={ActivityRawDataComponent}
          />
          <Route path="/crew-address" component={CrewAddressComponent} />
          <Route
            exact
            path="/crew-activity"
            component={CrewActivityComponent}
          />
          <Route
            path="/nonfly-activity-location"
            component={NonFlyActivityLocationComponent}
          />
          <Route
            path="/nonfly-transport-matrix"
            component={NonFlyTransportMatrixComponent}
          />
          <Route path="/activity-code" component={CrewActivityCodeComponent} />
          <Route
            path="/config/transport-matrix"
            component={TransportMatrixComponent}
          />
          <Route
            exact
            path="/crew-activity-mobile"
            component={CrewActivityMobileViewContainer}
          />
          </RightsGate>
          </Suspense>
        </AuthorisedLayout>
      ) : (
        <Layout>
          <Route exact path="/" component={LoginComponent} />
          <Route path="/login" component={LoginComponent} />
          <Route path="/forgot-password" component={ForgotPasswordComponent} />
          <Route path="/register" component={RegisterComponent} />
          <Route path="/reset-password" component={ResetPasswordComponent} />
        </Layout>
      )}
    </>
  );
}
export default App;
